import { createTheme } from "@mui/material/styles";

// Sabre brand palette — single source of truth for color across the app.
export const brand = {
  black: "#000000",
  white: "#FFFFFF",
  beige: "#F3EFE4",
  beigeTint: "#FAF7F2", // lighter-than-beige, used on top of paper (table headers/hover)
  terracotta: "#E2553C",
  terracottaDark: "#C23F28",
  terracottaLight: "#F0846F",
  terracottaTint: "#FBEAE6", // near-white terracotta wash for hover/subtle fills
  gold: "#EBC73A",
  goldDark: "#8A6F1D", // darkened for accessible text-on-light-gold
  goldLight: "#F5DC85",
  gray: "#B3B3B3", // the brand's "1px gray line" divider color
  grayLight: "#E5E5E5", // subtle borders on cards/tables (lighter than brand gray)
  grayDark: "#6B6B6B", // accessible muted text (brand gray fails contrast on white)
};

const theme = createTheme({
  palette: {
    primary: {
      main: brand.terracotta,
      dark: brand.terracottaDark,
      light: brand.terracottaLight,
      contrastText: brand.white,
    },
    secondary: {
      main: brand.gold,
      dark: brand.goldDark,
      light: brand.goldLight,
      contrastText: brand.black,
    },
    success: { main: "#16a34a", light: "#dcfce7", dark: "#15803d" },
    warning: { main: brand.gold, light: brand.goldLight, dark: brand.goldDark },
    error: { main: "#dc2626", light: "#fee2e2", dark: "#b91c1c" },
    info: { main: brand.grayDark, light: brand.grayLight },
    background: { default: brand.beige, paper: brand.white },
    text: { primary: brand.black, secondary: brand.grayDark },
    divider: brand.gray,
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', -apple-system, sans-serif",
    h4: { fontWeight: 700, letterSpacing: "-0.02em" },
    h5: { fontWeight: 700, letterSpacing: "-0.015em" },
    h6: { fontWeight: 600, letterSpacing: "-0.01em" },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600, color: brand.grayDark },
    button: { textTransform: "none", fontWeight: 600 },
    body2: { color: brand.grayDark },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: "thin",
          scrollbarColor: `${brand.gray} transparent`,
          "&::-webkit-scrollbar": { width: "6px", height: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: brand.gray,
            borderRadius: "3px",
            "&:hover": { background: brand.grayDark },
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${brand.grayLight}`,
          borderRadius: 12,
          transition: "box-shadow 0.2s ease",
          "&:hover": { boxShadow: "0 4px 24px rgba(0,0,0,0.08)" },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.elevation === 0
            ? { border: `1px solid ${brand.grayLight}`, borderRadius: 12 }
            : { borderRadius: 12 },
        elevation3: {
          border: "none",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: "0.875rem",
          padding: "8px 18px",
          transition: "all 0.15s ease",
        },
        containedPrimary: {
          backgroundColor: brand.terracotta,
          "&:hover": { backgroundColor: brand.terracottaDark },
        },
        containedSuccess: {
          backgroundColor: "#16a34a",
          "&:hover": { backgroundColor: "#15803d" },
        },
        outlinedPrimary: {
          borderColor: brand.terracottaLight,
          "&:hover": {
            backgroundColor: brand.terracottaTint,
            borderColor: brand.terracotta,
          },
        },
        sizeSmall: { padding: "5px 12px", fontSize: "0.8125rem" },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 600,
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: brand.grayDark,
            backgroundColor: brand.beigeTint,
            borderBottom: `2px solid ${brand.grayLight}`,
            padding: "12px 16px",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        body: {
          fontSize: "0.875rem",
          color: brand.black,
          padding: "14px 16px",
          borderBottom: `1px solid ${brand.grayLight}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color 0.1s ease",
          "&:hover td": { backgroundColor: brand.beigeTint },
          "&:last-child td": { borderBottom: "none" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 600, fontSize: "0.75rem" },
        colorSuccess: {
          backgroundColor: "#dcfce7",
          color: "#15803d",
          "& .MuiChip-icon": { color: "#15803d" },
        },
        colorWarning: {
          backgroundColor: brand.goldLight,
          color: brand.goldDark,
          "& .MuiChip-icon": { color: brand.goldDark },
        },
        colorError: {
          backgroundColor: "#fee2e2",
          color: "#b91c1c",
          "& .MuiChip-icon": { color: "#b91c1c" },
        },
        colorInfo: {
          backgroundColor: brand.grayLight,
          color: brand.grayDark,
          "& .MuiChip-icon": { color: brand.grayDark },
        },
        colorSecondary: {
          backgroundColor: brand.goldLight,
          color: brand.goldDark,
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", size: "small" },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            fontSize: "0.875rem",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: brand.terracotta,
            },
          },
          "& .MuiInputLabel-root": { fontSize: "0.875rem" },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
          backgroundColor: brand.grayLight,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: "0.875rem" },
        colorDefault: { backgroundColor: brand.terracotta, color: brand.white },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: "none", borderBottom: `1px solid ${brand.grayLight}` },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontSize: "0.875rem" },
        separator: { color: brand.gray },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: brand.gray } },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: brand.black,
          fontSize: "0.75rem",
          borderRadius: 6,
          padding: "6px 10px",
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: { padding: 0 },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: { fontWeight: 500 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: "0.875rem", borderRadius: 6 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${brand.grayLight}`,
          boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
          borderRadius: 10,
          padding: "4px",
        },
      },
    },
    MuiCircularProgress: {
      defaultProps: { size: 40 },
      styleOverrides: {
        colorPrimary: { color: brand.terracotta },
      },
    },
  },
});

export default theme;
